import React, { useState, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { ArrowDownIcon, ArrowRightIcon } from '@/common/assets/icon/icon';

// 섹션 타입 정의
export type SectionType = 'default' | 'clickable' | 'emphasized' | 'accordion' | 'notitled';

// 컬럼 수 정의
export type ColumnCount = 1 | 2 | 3 | 4;

// 섹션 컴포넌트 Props 정의
export interface SectionProps {
  type?: SectionType;
  title: ReactNode;
  titleSize?: 'sm' | 'md' | 'lg';
  description?: string;
  children?: ReactNode;
  onClick?: () => void;
  columns?: ColumnCount;
  className?: string;
  isOpen?: boolean;
  defaultOpen?: boolean;
  zIndex?: number;
  headerAction?: ReactNode; // 제목 우측에 들어갈 컴포넌트 (default 타입에서만 사용)
}

/**
 * 섹션 컴포넌트
 * 
 * @param {SectionType} type - 섹션 타입 (default, clickable, emphasized, accordion)
 * @param {string} title - 섹션 제목
 * @param {string} description - 섹션 설명
 * @param {ReactNode} children - 섹션 내용
 * @param {Function} onClick - 클릭 이벤트 핸들러
 * @param {ColumnCount} columns - 컬럼 수 (1-4)
 * @param {string} className - 추가 CSS 클래스
 * @param {boolean} isOpen - 아코디언 섹션이 열려있는지 여부 (controlled)
 * @param {boolean} defaultOpen - 아코디언 섹션의 초기 상태 (uncontrolled)
 */
const Section: React.FC<SectionProps> = ({
  type = 'default',
  title,
  titleSize = 'lg',
  description,
  children,
  onClick,
  columns = 1,
  className,
  isOpen: controlledIsOpen,
  defaultOpen = false,
  zIndex,
  headerAction,
}) => {
  // 아코디언 상태 관리 (controlled 또는 uncontrolled)
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(defaultOpen);
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : uncontrolledIsOpen;

  // 클릭 이벤트 핸들러
  const handleClick = () => {
    // 클릭 이벤트 콜백 실행
    if (onClick) {
      onClick();
    }
  };

  // 클릭 가능한 타입인지 확인
  const isClickable = type === 'clickable' || type === 'emphasized';

  // 아코디언 토글 핸들러
  const handleAccordionToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (type === 'accordion') {
      if (controlledIsOpen === undefined) {
        setUncontrolledIsOpen(!uncontrolledIsOpen);
      }
      if (onClick) {
        onClick();
      }
    }
  };

  return (
    <SectionContainer 
      className={`section-container ${className || ''}`}
      $type={type}
      $isClickable={isClickable}
      style={{ zIndex: zIndex }}
      onClick={isClickable ? handleClick : undefined}
    >
      {type !== 'notitled' && <SectionHeader 
        className="section-header"
        onClick={type === 'accordion' ? handleAccordionToggle : undefined} 
        style={{ cursor: type === 'accordion' ? 'pointer' : 'default' }}
      >
        <TitleWrapper className="title-wrapper">
          <SectionTitle $type={type} $titleSize={titleSize} className="section-title">{title}</SectionTitle>
          {(type === 'default' || type === 'accordion') && headerAction && (
            <HeaderActionContainer className="header-action-container">
              {headerAction}
            </HeaderActionContainer>
          )}
          {type === 'accordion' && (
            <AccordionIcon $isOpen={isOpen} className="accordion-icon">
              <ArrowDownIcon />
            </AccordionIcon>
          )}
          {type === 'clickable' && (
            <ArrowRightIcon />
          )}
        </TitleWrapper>
        {description && <SectionDescription className="section-desc">{description}</SectionDescription>}
      </SectionHeader>}
      
      {type === 'accordion' ? (
        <AccordionContent 
          $isOpen={isOpen}
          className="accordion-content"
        >
          <SectionContent $columns={columns} className="section-content">
            {children}
          </SectionContent>
        </AccordionContent>
      ) : children && (
        <SectionContent $columns={columns} className="section-content">
          {children}
        </SectionContent>
      )}
    </SectionContainer>
  );
};

// 스타일 컴포넌트 정의
const SectionContainer = styled.div<{ 
  $type: SectionType; 
  $isClickable: boolean;
}>`
  background-color: ${semanticColors.background.secondary};
  border-radius: 8px;
  padding: 24px;
  border: 1px solid ${semanticColors.border.primary};
  transition: all 0.2s ease;
  overflow: visible;
  
  ${props => props.$isClickable && css`
    cursor: pointer;
    
    &:hover {
      background-color: ${semanticColors.background.secondaryHover};
    }
  `}
  
  ${props => props.$type === 'emphasized' && css`
    border-left: 4px solid ${semanticColors.border.brand};
    
    &:hover {
      background-color: ${semanticColors.background.tertiary};
    }
  `}
  
  ${props => props.$type === 'accordion' && css`
    border: 1px solid ${semanticColors.border.primary};
    box-shadow: none;
  `}
`;

const SectionHeader = styled.div`
  
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const SectionTitle = styled.h6<{ $type: SectionType, $titleSize: 'sm' | 'md' | 'lg' }>`
  ${props => props.$titleSize === 'sm' && typography.subtitle.subtitle2}
  ${props => props.$titleSize === 'md' && typography.subtitle.subtitle1}
  ${props => props.$titleSize === 'lg' && typography.heading.h6}
  color: ${props => props.$type === 'emphasized' 
    ? semanticColors.text.brand 
    : semanticColors.text.primary};
  margin: 0 0 8px 0;
`;

const SectionDescription = styled.p`
  ${typography.paragraph.p2.medium}
  color: ${semanticColors.text.tertiary};
  margin: 0;
`;

const AccordionIcon = styled.span<{ $isOpen: boolean }>`
  color: ${semanticColors.icon.primary};
  transition: transform 0.3s ease;
  display: flex;
  align-items: center;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0)'};
`;

const HeaderActionContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
`;

// 아코디언 컨텐츠 컴포넌트
const AccordionContent = styled.div<{ 
  $isOpen: boolean;
}>`
  display: ${props => props.$isOpen ? 'block' : 'none'};
`;

const SectionContent = styled.div<{ $columns: ColumnCount }>`
  display: grid;
  grid-template-columns: repeat(${props => props.$columns}, 1fr);
  gap: 16px;
  margin-top: 16px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(${props => Math.min(props.$columns, 3)}, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(${props => Math.min(props.$columns, 2)}, 1fr);
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export default Section; 